import {
  Undo2,
  Redo2,
  Maximize,
  Save,
  FolderOpen,
  FilePlus,
  Cloud,
  X,
  Scissors,
  Copy,
  ClipboardPaste,
  HelpCircle,
  Grid3x3,
  Command,
  Image as ImageIcon,
  LayoutTemplate,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import { useFormattedShortcuts } from '../hooks/useCommandRegistry';
import type { CommandRegistry } from '../lib/command-registry';
import type { ViewSettings } from '../types/image-editor.types';

interface EditorToolbarProps {
  /** Command registry — the single source of truth for command
   *  metadata. Tooltips read shortcut hints from here so a user
   *  remapping `edit.undo` in Settings → Keyboard is reflected in
   *  the toolbar tooltip without a remount. */
  registry: CommandRegistry;
  /** Stable dispatch callback supplied by EditorPage. Every button +
   *  menu item routes through this — keyboard, palette, and toolbar
   *  share one firing path so the Phase 4 Action Recorder can capture
   *  every command from one chokepoint. */
  dispatch: (commandId: string) => void;
  canUndo: boolean;
  canRedo: boolean;
  /** True when there is an active tab (most file actions are disabled otherwise). */
  hasActiveFile: boolean;
  /** Cut & Copy require an active marquee. */
  canCutCopy: boolean;
  /** Paste requires a non-empty in-memory clipboard. */
  canPaste: boolean;
  /** Transparency checkerboard toggle (the underlay pattern that
   *  shows through transparent pixels). Distinct from the
   *  measurement grid feature in CanvasOverlays/Grid. */
  transparencyCheckerVisible: boolean;
  /** Tier 2 Group D View menu — current workspace toggle state.
   *  Display-only here; toggles dispatch through commands. */
  viewSettings: ViewSettings;
  /** True on macOS — passed through to useFormattedShortcut for
   *  platform-aware tooltip glyphs (⌘ vs Ctrl). */
  isMac: boolean;
}

export function EditorToolbar({
  registry,
  dispatch,
  canUndo,
  canRedo,
  hasActiveFile,
  canCutCopy,
  canPaste,
  transparencyCheckerVisible,
  viewSettings,
  isMac,
}: EditorToolbarProps) {
  // Tooltip hints are reactive — they re-render whenever the
  // registry mutates (initial load, override change, reset). This
  // is what keeps "Undo (Ctrl+Z)" in sync when a user remaps the
  // shortcut in Settings → Keyboard. Single subscription handles
  // every command's hint to avoid 12× useSyncExternalStore
  // subscribe/unsubscribe pairs per render.
  const hints = useFormattedShortcuts(
    registry,
    [
      'edit.undo',
      'edit.redo',
      'edit.cut',
      'edit.copy',
      'edit.paste',
      'view.fit-screen',
      'view.toggle-rulers',
      'view.toggle-grid',
      'view.toggle-guides',
      'palette.open',
    ] as const,
    isMac,
  );

  return (
    <div className="flex items-center h-12 bg-background border-b border-border px-2 gap-1">
      {/* File menu — folder icon expands to New / Save / Save As / Open / Close. */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="p-2 rounded-md hover:bg-accent text-muted-foreground"
            title="File"
            aria-label="File menu"
          >
            <FolderOpen className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48">
          <DropdownMenuItem onClick={() => dispatch('file.new')}>
            <FilePlus className="h-4 w-4 mr-2" />
            New
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => dispatch('file.new-from-template')}>
            <LayoutTemplate className="h-4 w-4 mr-2" />
            New from Template&hellip;
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => dispatch('file.open-local')}>
            <FolderOpen className="h-4 w-4 mr-2" />
            Open Local&hellip;
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => dispatch('file.open-fias')}>
            <Cloud className="h-4 w-4 mr-2" />
            Open Fias&hellip;
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => dispatch('file.save')} disabled={!hasActiveFile}>
            <Save className="h-4 w-4 mr-2" />
            Save
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => dispatch('file.save-as')} disabled={!hasActiveFile}>
            <Save className="h-4 w-4 mr-2" />
            Save As&hellip;
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => dispatch('file.save-as-template')}
            disabled={!hasActiveFile}
          >
            <LayoutTemplate className="h-4 w-4 mr-2" />
            Save as Template&hellip;
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {/* Tier 3 Group D Slice 1d — Document Settings (DPI / units /
              color mode metadata). Lives in the File menu rather than
              a standalone Image dropdown to conserve mobile toolbar
              space; the trade-off can be revisited if the Image menu
              grows more entries (Image Size, Canvas Size, Rotate). */}
          <DropdownMenuItem
            onClick={() => dispatch('image.document-settings')}
            disabled={!hasActiveFile}
          >
            <ImageIcon className="h-4 w-4 mr-2" />
            Document Settings&hellip;
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => dispatch('image.canvas-size')} disabled={!hasActiveFile}>
            <ImageIcon className="h-4 w-4 mr-2" />
            Canvas Size&hellip;
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => dispatch('file.close')} disabled={!hasActiveFile}>
            <X className="h-4 w-4 mr-2" />
            Close
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="w-px h-6 bg-border mx-1" />

      {/* Undo/Redo */}
      <ToolbarButton
        icon={Undo2}
        title={withHint('Undo', hints['edit.undo'])}
        onClick={() => dispatch('edit.undo')}
        disabled={!canUndo}
      />
      <ToolbarButton
        icon={Redo2}
        title={withHint('Redo', hints['edit.redo'])}
        onClick={() => dispatch('edit.redo')}
        disabled={!canRedo}
      />

      <div className="w-px h-6 bg-border mx-1" />

      {/* Edit — Cut / Copy / Paste. */}
      <ToolbarButton
        icon={Scissors}
        title={withHint('Cut', hints['edit.cut'])}
        onClick={() => dispatch('edit.cut')}
        disabled={!canCutCopy}
      />
      <ToolbarButton
        icon={Copy}
        title={withHint('Copy', hints['edit.copy'])}
        onClick={() => dispatch('edit.copy')}
        disabled={!canCutCopy}
      />
      <ToolbarButton
        icon={ClipboardPaste}
        title={withHint('Paste', hints['edit.paste'])}
        onClick={() => dispatch('edit.paste')}
        disabled={!canPaste}
      />

      <div className="w-px h-6 bg-border mx-1" />

      {/* Fit-to-screen — Zoom in / out and the % readout live in the
          StatusBar (bottom toolbar). Keeping Fit here because it's a
          one-shot action many users reach for via the top toolbar. */}
      <ToolbarButton
        icon={Maximize}
        title={withHint('Fit to screen', hints['view.fit-screen'])}
        onClick={() => dispatch('view.fit-screen')}
      />

      <div className="w-px h-6 bg-border mx-1" />

      {/* View menu — Tier 2 Group D rulers / grid / guides
          toggles + snap settings. Checkbox `checked` reads the
          current viewSettings; toggling dispatches the matching
          command so the keyboard, palette, and menu paths all
          share one firing site. */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="p-2 rounded-md text-muted-foreground hover:bg-accent"
            title="Grid/Guide settings"
            aria-label="Grid/Guide settings"
          >
            <Grid3x3 className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuCheckboxItem
            checked={viewSettings.rulersVisible}
            onCheckedChange={() => dispatch('view.toggle-rulers')}
          >
            Rulers
            {hints['view.toggle-rulers'] && (
              <span className="ml-auto text-xs text-muted-foreground">
                {hints['view.toggle-rulers']}
              </span>
            )}
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={viewSettings.gridVisible}
            onCheckedChange={() => dispatch('view.toggle-grid')}
          >
            Grid
            {hints['view.toggle-grid'] && (
              <span className="ml-auto text-xs text-muted-foreground">
                {hints['view.toggle-grid']}
              </span>
            )}
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={viewSettings.guidesVisible}
            onCheckedChange={() => dispatch('view.toggle-guides')}
          >
            Guides
            {hints['view.toggle-guides'] && (
              <span className="ml-auto text-xs text-muted-foreground">
                {hints['view.toggle-guides']}
              </span>
            )}
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={transparencyCheckerVisible}
            onCheckedChange={() => dispatch('view.toggle-transparency-checker')}
          >
            Transparency Checker
          </DropdownMenuCheckboxItem>
          <DropdownMenuSeparator />
          <DropdownMenuCheckboxItem
            checked={viewSettings.snapToGrid}
            onCheckedChange={() => dispatch('view.toggle-snap-grid')}
          >
            Snap to Grid
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={viewSettings.snapToGuides}
            onCheckedChange={() => dispatch('view.toggle-snap-guides')}
          >
            Snap to Guides
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={viewSettings.smartGuidesEnabled}
            onCheckedChange={() => dispatch('view.toggle-smart-guides')}
          >
            Smart Guides
            <span className="ml-auto text-xs text-muted-foreground">hold Ctrl=off</span>
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={viewSettings.lockGuides}
            onCheckedChange={() => dispatch('view.toggle-lock-guides')}
          >
            Lock Guides
          </DropdownMenuCheckboxItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => dispatch('view.new-guide')}>New Guide…</DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => dispatch('view.clear-guides')}
            disabled={viewSettings.guides.length === 0}
          >
            Clear Guides
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => dispatch('view.open-grid-settings')}>
            Grid Settings…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Command Palette + History + Help — right-aligned. Palette
          button is the sole entry point on mobile (no Cmd/Ctrl+K
          available); on desktop the keyboard shortcut still works
          and the tooltip shows the platform-correct hint. */}
      <button
        type="button"
        className="ml-auto p-2 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
        title={withHint('Command palette', hints['palette.open'])}
        aria-label={withHint('Open command palette', hints['palette.open'])}
        onClick={() => dispatch('palette.open')}
      >
        <Command className="h-4 w-4" />
      </button>
      {/* History-panel toggle removed — redundant with the History
          panel's own visibility. Still reachable via the
          `view.toggle-history-panel` command (palette + shortcut). */}
      <button
        type="button"
        className="p-2 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
        title="Show icon reference"
        aria-label="Show icon reference"
        onClick={() => dispatch('help.icon-legend')}
      >
        <HelpCircle className="h-4 w-4" />
      </button>
    </div>
  );
}

function withHint(label: string, hint: string): string {
  return hint ? `${label} (${hint})` : label;
}

function ToolbarButton({
  icon: Icon,
  title,
  onClick,
  disabled,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="p-2 rounded-md hover:bg-accent text-muted-foreground disabled:opacity-40 disabled:pointer-events-none"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}
